import { translate } from '@jsverse/transloco';

import { ComparisonHandlerService } from 'app/modules/dashboard/services/comparison-handler-service';
import { Comment, CommentPoint } from 'app/providers/bridge-api/dashboard.service';
import { TimezoneService } from 'app/services/timezone.service';
import { Utils } from 'app/utils/utils';
import { ProjectService } from '../../../../../providers/storage-api/project.service';
import { ReportService } from '../../../../../providers/storage-api/report.service';
import { FilterService } from '../../../../collection/services/filter.service';
import { WidgetChartOptions } from '../../../components/widget-chart/widget-chart.component';
import { AbstractFeatureChartReport } from '../chart-report-abstract/chart-report-abstract';

export class TaxonomyMapChartReport extends AbstractFeatureChartReport {
  reportName = 'taxonomyLevel';
  canExportAsPptx = false;
  sentimentRanges;

  constructor(
    public options: WidgetChartOptions,
    protected reportService: ReportService,
    protected filterService: FilterService,
    protected comparisonService: ComparisonHandlerService,
    protected timezoneService: TimezoneService,
    private projectService: ProjectService
  ) {
    super(options, reportService, filterService, comparisonService, timezoneService);
  }

  getFields() {
    return [];
  }

  canSort() {
    return false;
  }

  public canSetChartMaximum(): boolean {
    return false;
  }

  getColor(sentimentScale: string, sentimentScore: number) {
    //extreme value (will never come out of salience) to color mentions blue
    if (sentimentScore === 1000) {
      return '#0000ff';
    } else {
      return AbstractFeatureChartReport.getSentimentColor(this.sentimentRanges, sentimentScore);
    }
  }

  public async processResponse(report, _filterQuery?: string): Promise<any> {
    if (!this.sentimentRanges) {
      const project = await this.projectService.getProjectById(this.options.projectId, false);
      const analyticItem = this.options.analyticItem === 'documents' ? 'document' : 'feature';
      this.sentimentRanges = project.polarities[analyticItem][this.options.sentimentScale];
    }

    return report;
  }

  reportHandler = (dashboard, widget, response, _options) => {
    const sentimentScale = dashboard.payload.sentimentScale.toString();
    if (!response.items) {
      return null;
    }

    const points = [];

    for (const parentId of Object.getOwnPropertyNames(response.items)) {
      if (response.reportName === 'featureTopThemes') {
        let valueCount = 0;
        let topValue = 0;
        let topLabel = '';
        for (const childId of Object.getOwnPropertyNames(response.items[parentId])) {
          valueCount += response.items[parentId][childId];
          if (response.items[parentId][childId] > topValue) {
            topValue = response.items[parentId][childId];
            topLabel = childId;
          }
        }

        const colorValue = AbstractFeatureChartReport.getSentimentValueColor(
          widget.payload.sentimentScale,
          topLabel
        );
        const childNode = {
          id: 'id_' + parentId,
          name: parentId,
          value: valueCount,
          color: colorValue,
          isLeaf: false,
          pointId: parentId,
          pointDisplay: parentId,
          taxonomyPath: '',
          taxonomyDepth: 0,
          sentimentPolarity: topLabel,
          entityType: 'themes',
          documentCount: valueCount,
        };
        points.push(childNode);
      } else {
        const parentNode = {
          id: `id_${parentId}`,
          name: parentId,
        };
        for (const childId of Object.getOwnPropertyNames(response.items[parentId])) {
          let sentimentScore = response.items[parentId]?.[childId]?.sentimentScore ?? 1000;
          if (response.reportName === 'featurePhrases') {
            sentimentScore = response.items['sentiment']?.[childId] ?? 1000;
          }

          const colorValue = this.getColor(sentimentScale, sentimentScore);
          if (parentNode.id !== 'id_sentiment') {
            const childItem = response.items[parentId][childId];
            const childNode = {
              id: `${parentNode.id}_${childItem.path ? childItem.path + '|' : ''}${childId}`,
              name: childItem.name,
              value: childItem.count ?? childItem,
              color: colorValue,
              isLeaf: !!childItem.isLeaf,
              pointId: childId,
              pointDisplay: childItem.name,
              taxonomyPath: childItem.path ?? '',
              taxonomyDepth: childItem.depth ?? 0,
              sentimentScore,
              documentCount: childItem.count ?? childItem,
            };

            if (
              response.reportName === 'featureTopMentions' ||
              response.reportName === 'featurePhrases'
            ) {
              if (parentId === 'topic_count') {
                childNode['entityType'] = 'topics';
              } else {
                childNode['entityType'] = 'entities';
              }
              childNode['name'] = childId;
              childNode['pointDisplay'] = childId;
            }
            points.push(childNode);
          }
        }
      }
    }

    return {
      series: [
        {
          type: 'treemap',
          layoutAlgorithm: 'squarified',
          levelIsConstant: false,
          data: points,
          dataLabels: {
            style: {
              textOutline: 0,
            },
          },
        },
      ],
      tooltip: this.getTooltip(),
    };
  };

  canDrilldownMentions() {
    return true;
  }

  hasTaxonomyLevel() {
    return true;
  }

  canDrilldown() {
    return false;
  }

  getTooltip() {
    return {
      formatter() {
        if (this.point.sentimentPolarity) {
          return (
            translate('widgets.charting.tooltip.name-part', { name: this.point.name }) +
            '<br/>' +
            translate('widgets.charting.tooltip.document-count-part', {
              count: Utils.addThousandSeparator(this.point.value),
            }) +
            '<br/>' +
            translate('widgets.charting.tooltip.sentiment-polarity-part', {
              polarity: this.point.sentimentPolarity,
            })
          );
        }
        if (this.point.sentimentScore === 1000) {
          return (
            translate('widgets.charting.tooltip.name-part', { name: this.point.name }) +
            '<br/>' +
            translate('widgets.charting.tooltip.count-part', {
              count: Utils.addThousandSeparator(Math.round(this.point.value)),
            }) +
            '<br/>'
          );
        } else {
          return (
            translate('widgets.charting.tooltip.name-part', { name: this.point.name }) +
            '<br/>' +
            translate('widgets.charting.tooltip.document-count-part', {
              count: Utils.addThousandSeparator(this.point.value),
            }) +
            '<br/>' +
            translate('widgets.charting.tooltip.sentiment-score-part', {
              score:
                this.point.sentimentScore !== 'NaN' && this.point.sentimentScore !== 'Infinity'
                  ? this.point.sentimentScore.toFixed(1)
                  : 0.0,
            })
          );
        }
      },
    };
  }

  canHideItems() {
    return false;
  }

  chartItemToCommentPoint(item: any): CommentPoint {
    const point = super.chartItemToCommentPoint(item);
    point.point.y = item.value;
    return point;
  }

  protected getPointForComment(comment: Comment, series: any) {
    const point = super.getPointForComment(comment, series);
    if (point) {
      return { ...point, y: point.value };
    }
  }
}
